/**
 * What this register has been called, as bands over time.
 *
 * The bands come from the API. Which one is current, where a closed register stops, and how far
 * past a scheduled change the axis runs are the chart's whole content, and that is a derivation
 * the server owns. What is left here is geometry: the x-scale, the label pack, the edge clamping
 * and the 92-unit minimum below which a name is dropped.
 *
 * The accessible fallback is the lifecycle table beside it, which holds every fact the chart
 * draws. Nobody should try to make the SVG itself navigable.
 */

import { layoutLabelRows, measureLabel, linearScale, clampToChart, MINIMUM_BAND_WIDTH } from '~/lib/chart';
import { fmtDate, fmtYear } from '~/lib/format';
import type { LifecycleEvent, NameBand } from '~/api/schema';
import styles from './LifecycleBandChart.module.css';

const WIDTH = 1000;
const PADDING = 28;
const BAND_TOP = 96;
const BAND_HEIGHT = 34;
const LABEL_ROW_HEIGHT = 14;

export interface LifecycleBandChartProps {
  bands: readonly NameBand[];
  events: readonly LifecycleEvent[];
  registerName: string;
  /** The right-hand end of the axis: the closure, a scheduled change, or today. */
  axisEnd: string;
}

const KIND_LABEL: Record<string, string> = {
  opened: 'Opened',
  renamed: 'Renamed',
  closed: 'Closed',
};

export function LifecycleBandChart({
  bands,
  events,
  registerName,
  axisEnd,
}: LifecycleBandChartProps) {
  const nameBands = bands.filter((band) => band.field === 'name');
  const first = nameBands[0];
  if (!first) return null;

  // A register whose opening is undated (ADR-0025) has no left-hand end of its own, so the axis
  // starts at the earliest thing that does carry a date. With nothing dated at all there is no
  // axis to draw and no chart to draw on it.
  const axisStart =
    first.from ??
    events.map((event) => event.date).filter((date): date is string => Boolean(date))[0] ??
    null;
  if (!axisStart) return null;

  const scale = linearScale(axisStart, axisEnd, [PADDING, WIDTH - PADDING]);

  // An undated opening has no point on the axis, so it gets no marker. The band it starts still
  // draws, from the left-hand edge — the register existed, we only do not know from when.
  const measured = events
    .filter((event): event is typeof event & { date: string } => Boolean(event.date))
    .map((event) => {
      const x = scale(event.date);
      const flip = x > WIDTH * 0.72;
      return {
        event,
        ...measureLabel(`${KIND_LABEL[event.kind] ?? event.kind} ${fmtDate(event.date)}`, x, flip),
      };
    });

  const { rows, placed } = layoutLabelRows(measured);
  const labelBlock = rows * LABEL_ROW_HEIGHT;
  const bandTop = Math.max(BAND_TOP, labelBlock + 36);
  const height = bandTop + BAND_HEIGHT + 34;

  const description = nameBands
    .map(
      (band) =>
        `${band.value} from ${band.from ? fmtDate(band.from) : 'a date nobody has recorded'} to ${band.to ? fmtDate(band.to) : 'now'}`,
    )
    .join('; ');

  return (
    <div className={styles.scroll}>
      <svg
        className={styles.chart}
        viewBox={`0 0 ${WIDTH} ${height}`}
        role="img"
        aria-label={`Names ${registerName} has carried: ${description}. The same facts are in the lifecycle table below.`}
      >
        {placed.map(({ event, x, flip, row }) => {
          const tickX = clampToChart(x, PADDING, WIDTH - PADDING);
          const labelY = labelBlock - row * LABEL_ROW_HEIGHT;
          return (
            <g key={event.id}>
              <line
                x1={tickX}
                y1={labelY + 6}
                x2={tickX}
                y2={bandTop}
                className={[
                  styles.tick,
                  event.kind === 'opened' ? styles.tickOpened : undefined,
                  event.kind === 'closed' ? styles.tickClosed : undefined,
                ]
                  .filter(Boolean)
                  .join(' ')}
              />
              <circle
                cx={tickX}
                cy={bandTop}
                r={3}
                className={[
                  styles.dot,
                  event.kind === 'opened' ? styles.dotOpened : undefined,
                  event.kind === 'closed' ? styles.dotClosed : undefined,
                ]
                  .filter(Boolean)
                  .join(' ')}
              />
              <text
                x={tickX + (flip ? -6 : 6)}
                y={labelY}
                textAnchor={flip ? 'end' : 'start'}
                className={[
                  styles.eventLabel,
                  event.kind === 'closed' ? styles.eventLabelClosed : undefined,
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {KIND_LABEL[event.kind] ?? event.kind} {fmtDate(event.date)}
              </text>
            </g>
          );
        })}

        {nameBands.map((band) => {
          const left = clampToChart(scale(band.from ?? axisStart), PADDING, WIDTH - PADDING);
          const right = clampToChart(scale(band.to ?? axisEnd), PADDING, WIDTH - PADDING);
          const bandWidth = Math.max(2, right - left);
          return (
            <g key={`${band.from}-${band.value}`}>
              <rect
                x={left}
                y={bandTop}
                width={bandWidth}
                height={BAND_HEIGHT}
                rx={6}
                className={[
                  styles.band,
                  band.current ? styles.bandCurrent : undefined,
                  band.scheduled ? styles.bandScheduled : undefined,
                ]
                  .filter(Boolean)
                  .join(' ')}
              />
              {bandWidth > MINIMUM_BAND_WIDTH ? (
                <text x={left + 10} y={bandTop + 21} className={styles.bandName}>
                  {band.value}
                </text>
              ) : null}
              <text
                x={clampToChart(left, PADDING, WIDTH - PADDING - 60)}
                y={bandTop + BAND_HEIGHT + 16}
                className={styles.bandSpan}
              >
                {band.from ? fmtYear(band.from) : 'unknown'}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
